import { Fragment } from 'react';

import styled from 'styled-components';

import { Alert } from '@components/alert';
import { Card } from '@components/card';
import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconIntegration } from '@components/icons/integration';
import { IconSend } from '@components/icons/send';
import { InfoBox } from '@components/info-box';
import { useIntegrationEndpoint } from '@components/integrations';
import {
  IntegrationEndpoint,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { fileNameFromPath, isBase64File } from '@utils/strings';

import { createTravellerJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import {
  useIntegrationTravellerActionFile,
  useTravellerActionIntegrationEndpoint,
} from 'api/integrations/hooks';
import { FormFile } from 'api/journey-action-forms/requests';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { IntegrationField, processVariableValue } from './journey-sidebar';

import {
  ActionResultCard,
  Base64DownlodableLink,
  EditableActionContent,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TriggerMethod,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'integration';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  const {
    data: integration,
    isLoading: endpointIsLoading,
    isError: endpointIsError,
  } = useTravellerActionIntegrationEndpoint(shortId);
  if (travellerActionQuery.isLoading || endpointIsLoading) {
    return <RelativeSpinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={integration?.name ?? t('title')}
        icon={IconIntegration}
        iconColor="green200"
        customIcon={
          integration?.thumbnail ? <SquareImage src={integration.thumbnail} /> : undefined
        }
      >
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        {endpointIsError ? (
          <InfoBox variant="red" icon={<IconFormIndicatorError />} text={t('integrationError')} />
        ) : (
          <EditableActionContent
            edit={<FormContent action={action} />}
            display={<DetailsContent action={action} />}
          />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { data, isLoading } = useIntegrationEndpoint(
    action.values?.integrationEndpointId.value ?? ''
  );
  if (isLoading || !data) {
    return <Spinner />;
  }
  const status = getActionStatus(action);
  const isError = isActionError(action);
  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={4}>
      <LabelTextGroup label={t('description')} text={action.description} />
      <ActionResultCard
        action={action}
        title={data?.name ?? t('title')}
        icon={data?.thumbnail ? <SquareImage src={data.thumbnail} /> : <IconIntegration />}
        progressText={progressText}
        errorTitle={actionErrorTitle[action.action]}
      >
        <Stack space={4}>
          {action.status === 'completed' && <CompletedContent action={action} data={data} />}
          {action.status !== 'completed' && <IncompleteContent action={action} data={data} />}
        </Stack>
      </ActionResultCard>
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('run')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

type ArrayFieldValue = Array<Record<string, string | ArrayFieldValue>>;

const RenderArrayField = ({
  field,
  value,
}: {
  field: IntegrationEndpointVariable;
  value?: ArrayFieldValue;
}) => {
  const showFilePreviewModal = usePreviewFileModal();
  if (!value) {
    return <LabelTextGroup key={field.name} label={field.label} />;
  }
  return (
    <Stack space={2}>
      <Text font="semibold14" key={field.name} text={field.label} />
      {(value ?? []).map((item: Record<string, string | ArrayFieldValue | FormFile>, idx) => (
        <Card space={4} key={idx}>
          <Stack space={2}>
            {(field.template ?? [])
              .filter((template) => template.visible)
              .map((template) => {
                if (template.type === 'array') {
                  return (
                    <RenderArrayField
                      key={template.name}
                      field={template}
                      value={item[template.name] as ArrayFieldValue}
                    />
                  );
                }
                if (template.type === 'file' && item[template.name]) {
                  const data = item[template.name];
                  const isObject = typeof data === 'object';
                  const file: FormFile = isObject
                    ? (data as FormFile)
                    : {
                        name: fileNameFromPath(data as string),
                        src: data as string,
                      };
                  return (
                    <UploadedFile
                      name={file.name}
                      path={file.src}
                      onPreview={() => showFilePreviewModal({ formFile: file, filesList: [file] })}
                      isDisabled={!isObject}
                    />
                  );
                }
                return (
                  <LabelTextGroup
                    key={template.name}
                    label={template.label}
                    text={item[template.name] as string}
                  />
                );
              })}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};

const getRequestDataText = (
  action: TravellerJourneyActionDetailed<'integration'>,
  field: IntegrationEndpointVariable
) => {
  const value = action.data.requestData[field.name];
  const display =
    field.type === 'select' && field.options
      ? field.options.find((option) => value === option.value)?.label ?? value
      : field.type === 'boolean'
      ? String(value ?? false)
      : value;
  return field.type === 'date' && value ? abbreviateDate(value, 'en') : display;
};

type ExtendedContentProps = ContentProps & {
  data: IntegrationEndpoint;
};

const CompletedContent = ({ action, data }: ExtendedContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showFilePreviewModal = usePreviewFileModal();
  const variables = data.variables.filter((variable) => variable.visible);

  const responseData = action.data.responseData;
  const hasNumericFileId = typeof responseData?.file === 'number';
  const fileQuery = useIntegrationTravellerActionFile(hasNumericFileId ? action.shortId : '');
  const fetchedFilePath = fileQuery.data?.file;
  const fallbackFilePath = hasNumericFileId ? fetchedFilePath : responseData?.file;
  const showFallbackFile = !data.responseValues && responseData?.file && fallbackFilePath;

  return (
    <>
      <Text font="heading2" text={t('requestData')} />
      {variables.map((field) => {
        const value = action.data.requestData[field.name];
        if (field.type === 'array') {
          return <RenderArrayField key={field.name} field={field} value={value} />;
        }
        return (
          <Fragment key={field.name}>
            {isBase64File(value) ? (
              <div>
                <Text font="semibold14" text={field.label} />
                <Base64DownlodableLink base64string={value} showAsCard />
              </div>
            ) : typeof value === 'object' && value?.src ? (
              <UploadedFile
                name={value.name ?? fileNameFromPath(value.src)}
                path={value.src}
                iconColor="elementBlue100"
                font="regular14"
                onPreview={() => {
                  showFilePreviewModal({
                    formFile: {
                      src: value.src,
                      name: value.name ?? fileNameFromPath(value.src),
                    },
                    filesList: [
                      { src: value.src, name: value.name ?? fileNameFromPath(value.src) },
                    ],
                  });
                }}
              />
            ) : (
              <LabelTextGroup
                key={field.name}
                label={field.label}
                text={getRequestDataText(action, field)}
              />
            )}
          </Fragment>
        );
      })}
      {data.responseValues && (
        <>
          <Text font="heading2" text={t('responseData')} />
          {data.responseValues.map((field) => {
            const fieldValue = responseData
              ? responseData[field.name]
              : // Backwards compatibility
                action.data[field.name];

            if (field.type === 'file' && fieldValue) {
              const isObject = typeof fieldValue === 'object';
              const isNumericId = typeof fieldValue === 'number';

              const resolvedFilePath = isNumericId ? fetchedFilePath : (fieldValue as string);

              if (isNumericId && !fetchedFilePath) {
                return (
                  <Stack space={2} key={field.name}>
                    <Text font="semibold14" text={field.label} />
                  </Stack>
                );
              }

              const fileData: FormFile = isObject
                ? (fieldValue as FormFile)
                : {
                    name: fileNameFromPath(resolvedFilePath ?? ''),
                    src: resolvedFilePath ?? '',
                  };

              return (
                <Stack space={2} key={field.name}>
                  <Text font="semibold14" text={field.label} />
                  <UploadedFile
                    name={fileData.name}
                    path={fileData.src}
                    onPreview={() =>
                      showFilePreviewModal({
                        formFile: fileData,
                        filesList: [fileData],
                      })
                    }
                    iconColor="elementPurpleInvert"
                    font="regular14"
                  />
                </Stack>
              );
            }

            return (
              <LabelTextGroup
                key={field.name}
                label={field.label}
                text={
                  typeof field !== 'object'
                    ? action.data[field]
                    : fieldValue
                    ? JSON.stringify(fieldValue)
                    : ''
                }
              />
            );
          })}
        </>
      )}
      {showFallbackFile && (
        <>
          <Text font="heading2" text={t('responseData')} />
          <Stack space={2}>
            <UploadedFile
              name={fileNameFromPath(fallbackFilePath)}
              path={fallbackFilePath}
              onPreview={() =>
                showFilePreviewModal({
                  formFile: {
                    name: fileNameFromPath(fallbackFilePath),
                    src: fallbackFilePath,
                  },
                  filesList: [
                    {
                      name: fileNameFromPath(fallbackFilePath),
                      src: fallbackFilePath,
                    },
                  ],
                })
              }
              iconColor="elementPurpleInvert"
              font="regular14"
            />
          </Stack>
        </>
      )}
    </>
  );
};

const IncompleteContent = ({ action, data }: ExtendedContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showFilePreviewModal = usePreviewFileModal();
  const variables = data.variables.filter((variable) => variable.visible);
  return (
    <>
      {action.result?.status === 'error' && (
        <>
          <Text font="heading2" text={t('errorResponse')} />
          <Alert variant="error">
            <CodeBlock>{JSON.stringify(action.result.validationError, null, 2)}</CodeBlock>
          </Alert>
          <Text font="heading2" text={t('requestData')} />
        </>
      )}
      {variables.map((field) => {
        if (field.type === 'array') {
          return (
            <RenderArrayField
              key={field.name}
              field={field}
              value={action.data.requestData[field.name]}
            />
          );
        }
        if (field.type === 'file') {
          const file = action.data.requestData[field.name];
          return (
            <Stack space={2} key={field.name}>
              <Text font="semibold14" text={field.label} />
              {file ? (
                <UploadedFile
                  name={file.name ?? fileNameFromPath(file)}
                  path={file.src ?? ''}
                  onPreview={
                    file.src
                      ? () => {
                          showFilePreviewModal({
                            formFile: file,
                            filesList: [file],
                          });
                        }
                      : undefined
                  }
                  iconColor="elementBlue100"
                  font="regular14"
                />
              ) : (
                <Text font="regular14" color="textHelper" text={t('core.not_set')} />
              )}
            </Stack>
          );
        }
        return (
          <LabelTextGroup
            key={field.name}
            label={field.label}
            text={getRequestDataText(action, field)}
          />
        );
      })}
    </>
  );
};

const CodeBlock = styled.pre(() => ({
  'word-wrap': 'break-word',
  'white-space': 'pre-wrap',
}));

type IntegrationData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  integrationId: string;
} & Record<string, { value: string; variables: object } | string>;

const Form = createForm<IntegrationData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const { data, isLoading } = useIntegrationEndpoint(action.data.integrationEndpointId);
  const variables = (data?.variables ?? []).filter((variable) => variable.visible);

  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const uploadMutation = useUploadFile(createTravellerJourneyActionDocumentPath(action), true);
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const extractRequestData = (data: IntegrationData) => {
    const prefix = 'requestData__';
    const requestData: { [key: string]: any } = {};

    const setArrayValues = (obj: any, path: (string | number)[], value: any) => {
      let current = obj;
      for (let i = 0; i < path.length - 1; i++) {
        const part = path[i];
        if (!current[part]) {
          if (isNaN(Number(path[i + 1]))) {
            current[part] = {};
          } else {
            current[part] = [];
          }
        }
        current = current[part];
      }
      current[path[path.length - 1]] = value;
    };

    Object.keys(data).forEach((key) => {
      if (key.startsWith(prefix)) {
        const newKey = key.substring(prefix.length);
        const value = data[key];
        if (value === undefined) return;
        let processedValue = value;
        if (typeof value !== 'object') {
          processedValue = { value, variables: {} };
        }
        const variable = variables.find((v) => v.name === newKey);
        if (variable) {
          if (typeof processedValue === 'object') {
            processedValue = processVariableValue(
              variable,
              processedValue
            ) as typeof processedValue;
          }
          requestData[newKey] = processedValue;
        } else {
          const path = newKey
            .split('__')
            .map((part) => (isNaN(Number(part)) ? part : Number(part)));
          setArrayValues(requestData, path, processedValue);
        }
      }
    });

    return requestData;
  };

  const handleSave = async (data: IntegrationData) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: 'integration',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        integrationEndpointId: { value: action.data.integrationEndpointId },
        requestData: extractRequestData(data),
        continueOnError: data.continueOnError,
      },
    });
  };

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['email'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['date'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['select', 'multi_select'],
  });

  const getVariableGroups = (fieldType: string) => {
    switch (fieldType) {
      case 'email':
        return emailVariableGroups;
      case 'date':
        return dateVariableGroups;
      case 'select':
        return optionsVariableGroups;
      default:
        return variableGroups;
    }
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack orientation="vertical" space={6}>
        <DescriptionFormField defaultValue={action.description} />
        <ExecutionMethod
          assignees={action.assignees}
          isAutomatic={action.isAutomatic}
          formStore={formStore}
        />
        <Separator />
        <HideContainer title={t('advanced')} defaultClosed>
          <Form.Checkbox
            // @ts-ignore
            id="continueOnError"
            label={t('continueOnError')}
            defaultValue={action.values?.continueOnError ?? false}
          />
        </HideContainer>
        {isLoading && <Spinner />}
        {variables.map((field) => (
          <IntegrationField
            key={field.name}
            field={field}
            allFields={variables}
            defaultValue={action.values?.requestData?.[field.name]}
            variableGroups={getVariableGroups(field.type)}
            uploadMutation={uploadMutation}
            fieldMappingId={fieldMappingId}
          />
        ))}
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};
const sidebarLocale = {
  en: {
    title: 'Integration',
    dataNotFound: 'Data not found',
    description: 'Description',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    file: 'File',
    started: 'Started',
    completed: 'Completed',
    run: 'Run',
    retry: 'Retry',
    template: 'Template',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    requestData: 'Request data',
    responseData: 'Response data',
    errorResponse: 'Error response',
    addMore: 'Add more',
    integrationError:
      'Failed to load integration, if the integration has been deleted please replace the action in the journey builder',
    advanced: 'Advanced',
    continueOnError: 'Continue on error',
  },
};
